import { useState, useEffect, useRef } from "react"
import { Box, Card, TextField, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { buildSimpleTextFragment, parseSimpleTextFragment } from "@crm-src/lib/email-fragment-builders"

interface SimpleTextEmailBuilderProps {
  tenantInfo?: {
    name?: string
    logo_url?: string | null
    email?: string | null
    website?: string | null
    phone?: string | null
    address?: string | null
  }
  initialContent?: string
  onContentChange: (html: string) => void
}

// The builder emits a body FRAGMENT only (see email-fragment-builders.ts) —
// crm-api's mailer adds the CID brand logo, the personalized "Hello <name>,"
// greeting and the tenant footer. Emitting a complete email here is what caused
// the double-wrapped "two logos / two footers" bug. Inline images pasted into
// the editor (base64) are CID-embedded by crm-api at send, so they render in
// Gmail and everywhere else.
export default function SimpleTextEmailBuilder({
  tenantInfo,
  initialContent,
  onContentChange
}: SimpleTextEmailBuilderProps) {
  // A saved campaign's content is the WHOLE fragment (body + signature
  // block). Split it back apart — loading it as the body is what stacked a
  // new "Best regards" onto the email on every reopen.
  const [initial] = useState(() =>
    initialContent ? parseSimpleTextFragment(initialContent) : null
  )
  const [content, setContent] = useState<string>(
    initial?.body || '<p>Write your message here...</p>'
  )
  const [signature, setSignature] = useState<string>(
    initial ? (initial.signature ?? '') : (tenantInfo?.name || 'Your Name')
  )

  const lastEmittedRef = useRef<string | null>(null)

  // Emit the fragment whenever the message body or signature changes.
  useEffect(() => {
    const html = buildSimpleTextFragment(content, signature)
    lastEmittedRef.current = html
    onContentChange(html)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, signature])

  // The form re-hydrates from the fetched campaign AFTER this mounts, handing
  // the stored fragment back and overwriting what we emitted. Anything we did
  // not emit ourselves is split again and the rebuilt fragment re-emitted, so
  // the saved email always carries exactly one sign-off.
  useEffect(() => {
    if (!initialContent || initialContent === lastEmittedRef.current) return
    const parsed = parseSimpleTextFragment(initialContent)
    const body = parsed.body || '<p>Write your message here...</p>'
    const sig = parsed.signature ?? ''
    setContent(body)
    setSignature(sig)
    const html = buildSimpleTextFragment(body, sig)
    if (html !== initialContent) {
      lastEmittedRef.current = html
      onContentChange(html)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContent])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Message body */}
      <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Email Content</Typography>
          <InlineAiGenerator
            fieldType="long"
            context="professional email message"
            placeholder="e.g., product announcement, event invitation"
            currentValue={content}
            onGenerate={setContent}
          />
        </Box>
        <TipTapEditor
          value={content}
          onChange={setContent}
          placeholder="Write your email message here..."
          minHeight={300}
        />
      </Card>

      {/* Signature */}
      <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Signature (Optional)</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="e.g., John Doe, Sales Team, etc."
          />
          <InlineAiGenerator
            fieldType="short"
            context="email signature name"
            placeholder="e.g., professional signature"
            currentValue={signature}
            onGenerate={setSignature}
          />
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Leave empty for no "Best regards" sign-off.</Typography>
      </Card>
      {/* No inline preview — the always-visible right-hand "Live Preview" pane
          renders this email (logo + "Hello <name>," + body + footer). */}
    </Box>
  )
}
